import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { createItem } from "@/lib/api";
import { Star, Send, Loader2 } from "lucide-react";
import { analytics } from "@/lib/analytics";

const CATEGORIES = ["General", "Bug Report", "Feature Request", "Compliment", "Content Concern"];

export function FeedbackSection() {
  const [form, setForm]       = useState({ name: "", email: "", category: "General", message: "" });
  const [rating, setRating]   = useState(5);
  const [hover, setHover]     = useState(0);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) { toast.error("Please share a message"); return; }
    setLoading(true);
    try {
      await createItem("feedback", { name: form.name || "Anonymous", email: form.email || null, category: form.category, rating, message: form.message });
      analytics.submitFeedback(form.category, rating);
      toast.success("Thank you!", { description: "We read every single message." });
      setForm({ name: "", email: "", category: "General", message: "" });
      setRating(5);
    } catch {
      toast.error("Couldn't send feedback", { description: "Please try again shortly." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="feedback" className="py-20 sm:py-24 bg-[#f5f5f7] border-t border-[#d2d2d7]">
      <div className="max-w-[600px] mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55 }}
          className="text-center mb-10"
        >
          <p className="text-[13px] font-semibold tracking-[0.08em] uppercase text-[#6e6e73] mb-3">Feedback</p>
          <h2
            className="text-[40px] sm:text-[48px] font-bold text-[#1d1d1f] mb-4"
            style={{ letterSpacing: "-0.03em", lineHeight: "1.06" }}
          >
            Help us build NC Desi better.
          </h2>
          <p className="text-[16px] text-[#6e6e73] leading-[1.55]">
            This platform exists for you. Tell us what's broken, what's missing, or what you love.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55, delay: 0.1 }}
          onSubmit={submit}
          className="rounded-2xl border border-[#d2d2d7] bg-white p-6 sm:p-8 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[14px] text-[#1d1d1f]">Name</Label>
              <Input placeholder="Optional" value={form.name} onChange={e => set("name", e.target.value)}
                className="h-[44px] rounded-xl border-[#d2d2d7] focus:border-[#0071e3] text-[15px]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[14px] text-[#1d1d1f]">Email</Label>
              <Input placeholder="Optional" value={form.email} onChange={e => set("email", e.target.value)}
                className="h-[44px] rounded-xl border-[#d2d2d7] focus:border-[#0071e3] text-[15px]" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <div className="space-y-1.5">
              <Label className="text-[14px] text-[#1d1d1f]">Category</Label>
              <Select value={form.category} onValueChange={v => set("category", v)}>
                <SelectTrigger className="h-[44px] rounded-xl border-[#d2d2d7] text-[15px]"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[14px] text-[#1d1d1f]">How's your experience?</Label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                    className="p-1 transition-transform hover:scale-110" aria-label={`Rate ${n}`}>
                    <Star className={`w-6 h-6 transition-colors ${n <= (hover || rating) ? "fill-[#0071e3] text-[#0071e3]" : "text-[#d2d2d7]"}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[14px] text-[#1d1d1f]">
              Your message <span className="text-[#0071e3]">*</span>
            </Label>
            <Textarea
              rows={4}
              className="resize-none rounded-xl border-[#d2d2d7] focus:border-[#0071e3] text-[15px]"
              placeholder="What's on your mind?"
              value={form.message}
              onChange={e => set("message", e.target.value)}
            />
          </div>

          <Button type="submit" disabled={loading} size="lg" className="w-full">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Feedback
          </Button>
        </motion.form>
      </div>
    </section>
  );
}
